import { rpc, xdr } from '@stellar/stellar-sdk';

import { CONTRACTS, TIMELOCK_CONTRACT_METHOD } from 'constants/soroban';
import { ACCOUNT_FOR_SIMULATE } from 'constants/stellar-accounts';

import { getEnv } from 'helpers/env';
import { SorobanPrepareTxErrorHandler } from 'helpers/error-handler';

import type Connection from 'services/soroban/connection/connection';
import {
    LockerTransactions,
    type LockerTransactionSigner,
} from 'services/soroban/contracts/lockerPreparation';
import { buildBatchCall, simulateBatch } from 'services/soroban/utils/connectionBatchSimulation';
import { publicKeyToScVal, u64ToScVal } from 'services/soroban/utils/scValHelpers';

// Contract maximum for get_positions_page and get_cb_page.
export const TIMELOCK_PAGE_LIMIT = 32;

// A stale envelope means the owner's index changed between pages; paging restarts from the
// beginning at most this many times before the read is reported as failed.
const MAX_PAGING_ATTEMPTS = 3;

const STALE_DIRECT_CURSOR_ERROR = 'Error(Contract, #2453)';
const STALE_CB_CURSOR_ERROR = 'Error(Contract, #2431)';

const U32_MAX = 0xffffffff;

export type DirectPositionKind = 'Active' | 'Unlocking';

export interface DirectPosition {
    positionId: number;
    // u64 stroops
    amount: bigint;
    // Unix seconds; null only for Active, which has a notice period instead of an end.
    end: number | null;
    notice: number | null;
    kind: DirectPositionKind;
    start: number;
}

export type DirectPositionStatus =
    | { status: 'Missing' }
    | { status: 'Active'; position: DirectPosition }
    | { status: 'Closed'; position: DirectPosition };

export interface DirectPageEnvelope {
    directIndexRevision: bigint;
    activeDirectCount: number;
}

export type CbRepresentation = 'Pinned' | 'Materialized';

export interface CbPosition {
    // 72-character lowercase hex, the same id Horizon uses for the claimable balance.
    cbId: string;
    // u64 stroops
    amount: bigint;
    // Raw Classic claim time (unix seconds).
    tRaw: number;
    // Entitlement end: the Monday 00:00 UTC on or before tRaw.
    end: number;
    predicateHash: string;
    representation: CbRepresentation;
}

export interface CbPageEnvelope {
    cbIndexRevision: bigint;
    activeCbCount: number;
}

export interface TimelockPage<Item, Envelope> {
    items: Item[];
    nextCursor: number;
    exhausted: boolean;
    envelope: Envelope;
}

export class TimelockDecodeError extends Error {
    constructor(message: string) {
        super(`Unexpected locker contract response: ${message}`);
        this.name = 'TimelockDecodeError';
    }
}

export class TimelockPagingError extends Error {
    constructor(message: string) {
        super(`Inconsistent locker contract pages: ${message}`);
        this.name = 'TimelockPagingError';
    }
}

function fail(message: string): never {
    throw new TimelockDecodeError(message);
}

const expectType = <Type extends xdr.ScVal['type']>(
    value: xdr.ScVal | undefined,
    type: Type,
    what: string,
): Extract<xdr.ScVal, { type: Type }> => {
    if (!value || !xdr.isUnionVariant(value, type)) {
        fail(`${what} must be ${type}`);
    }
    return value;
};

const decodeU32 = (value: xdr.ScVal | undefined, what: string): number =>
    expectType(value, 'scvU32', what).u32;

const decodeU64 = (value: xdr.ScVal | undefined, what: string): bigint =>
    expectType(value, 'scvU64', what).u64;

// u64 seconds kept as a number only when it is exactly representable.
const decodeSeconds = (value: xdr.ScVal | undefined, what: string): number => {
    const seconds = decodeU64(value, what);

    if (seconds > BigInt(Number.MAX_SAFE_INTEGER)) {
        fail(`${what} is out of range`);
    }
    return Number(seconds);
};

const decodeBool = (value: xdr.ScVal | undefined, what: string): boolean =>
    expectType(value, 'scvBool', what).b;

const decodeBytesHex = (value: xdr.ScVal | undefined, length: number, what: string): string => {
    const bytes = expectType(value, 'scvBytes', what).bytes.toBytes();

    if (bytes.length !== length) {
        fail(`${what} must be ${length} bytes`);
    }
    return Buffer.from(bytes).toString('hex');
};

const decodeVec = (value: xdr.ScVal | undefined, what: string): xdr.ScVal[] =>
    expectType(value, 'scvVec', what).vec ?? fail(`${what} must be a vector`);

const decodeOption = <T>(
    value: xdr.ScVal | undefined,
    decode: (inner: xdr.ScVal) => T,
): T | null => (value?.type === 'scvVoid' ? null : decode(value));

// A #[contracttype] struct is a map keyed by its field names; any missing, extra or repeated
// field is rejected.
const decodeStruct = <Field extends string>(
    value: xdr.ScVal | undefined,
    fields: readonly Field[],
    what: string,
): Record<Field, xdr.ScVal> => {
    const entries = expectType(value, 'scvMap', what).map ?? fail(`${what} must be a map`);
    const result = {} as Record<Field, xdr.ScVal>;
    const seen = new Set<string>();

    entries.forEach(entry => {
        const key = expectType(entry.key, 'scvSymbol', `${what} key`).sym.toString();

        if (!(fields as readonly string[]).includes(key) || seen.has(key)) {
            fail(`${what} has an unexpected field ${key}`);
        }
        seen.add(key);
        result[key as Field] = entry.val;
    });

    if (seen.size !== fields.length) {
        fail(`${what} must have the fields ${fields.join(', ')}`);
    }
    return result;
};

// A #[contracttype] enum is a vector of the variant symbol followed by its payload.
const decodeEnum = (
    value: xdr.ScVal | undefined,
    what: string,
): { variant: string; payload: xdr.ScVal[] } => {
    const [tag, ...payload] = decodeVec(value, what);

    return {
        variant: expectType(tag, 'scvSymbol', `${what} variant`).sym.toString(),
        payload,
    };
};

const decodeUnitEnum = <Variant extends string>(
    value: xdr.ScVal | undefined,
    variants: readonly Variant[],
    what: string,
): Variant => {
    const { variant, payload } = decodeEnum(value, what);

    if (!(variants as readonly string[]).includes(variant) || payload.length) {
        fail(`${what} has an unknown variant ${variant}`);
    }
    return variant as Variant;
};

const DIRECT_POSITION_KINDS: readonly DirectPositionKind[] = ['Active', 'Unlocking'];
const CB_REPRESENTATIONS: readonly CbRepresentation[] = ['Pinned', 'Materialized'];

export const parseDirectPosition = (value: xdr.ScVal | undefined): DirectPosition => {
    const fields = decodeStruct(
        value,
        ['amount', 'end', 'kind', 'notice', 'position_id', 'start'] as const,
        'direct position',
    );
    const kind = decodeUnitEnum(fields.kind, DIRECT_POSITION_KINDS, 'direct position kind');
    const end = decodeOption(fields.end, inner => decodeSeconds(inner, 'direct position end'));
    const notice = decodeOption(fields.notice, inner =>
        decodeSeconds(inner, 'direct position notice'),
    );
    const isActive = kind === 'Active';

    if (isActive ? end !== null || notice === null : end === null || notice !== null) {
        fail(`direct position of kind ${kind} has an unexpected end/notice shape`);
    }

    return {
        positionId: decodeU32(fields.position_id, 'direct position id'),
        amount: decodeU64(fields.amount, 'direct position amount'),
        end,
        notice,
        kind,
        start: decodeSeconds(fields.start, 'direct position start'),
    };
};

export const parseDirectPositionStatus = (value: xdr.ScVal | undefined): DirectPositionStatus => {
    const { variant, payload } = decodeEnum(value, 'direct position status');

    if (variant === 'Missing' && payload.length === 0) {
        return { status: 'Missing' };
    }
    if ((variant === 'Active' || variant === 'Closed') && payload.length === 1) {
        return { status: variant, position: parseDirectPosition(payload[0]) };
    }
    return fail(`direct position status has an unknown variant ${variant}`);
};

export const parseDirectPage = (
    value: xdr.ScVal | undefined,
): TimelockPage<DirectPosition, DirectPageEnvelope> => {
    const fields = decodeStruct(
        value,
        ['envelope', 'exhausted', 'items', 'next_cursor'] as const,
        'direct page',
    );
    const envelope = decodeStruct(
        fields.envelope,
        ['active_direct_count', 'direct_index_revision'] as const,
        'direct page envelope',
    );

    return {
        items: decodeVec(fields.items, 'direct page items').map(parseDirectPosition),
        nextCursor: decodeU32(fields.next_cursor, 'direct page cursor'),
        exhausted: decodeBool(fields.exhausted, 'direct page exhausted'),
        envelope: {
            directIndexRevision: decodeU64(envelope.direct_index_revision, 'direct revision'),
            activeDirectCount: decodeU32(envelope.active_direct_count, 'direct count'),
        },
    };
};

export const parseCbPosition = (value: xdr.ScVal | undefined): CbPosition => {
    const fields = decodeStruct(
        value,
        ['amount', 'cb_id', 'end', 'predicate_hash', 'representation', 't_raw'] as const,
        'imported lock',
    );
    const cbId = decodeBytesHex(fields.cb_id, 36, 'imported lock id');

    // The id is the Classic claimable balance id: a zero (V0) type prefix and a 32-byte hash.
    if (!cbId.startsWith('00000000')) {
        fail('imported lock id has an unknown claimable balance type');
    }

    return {
        cbId,
        amount: decodeU64(fields.amount, 'imported lock amount'),
        tRaw: decodeSeconds(fields.t_raw, 'imported lock claim time'),
        end: decodeSeconds(fields.end, 'imported lock end'),
        predicateHash: decodeBytesHex(fields.predicate_hash, 32, 'imported lock predicate'),
        representation: decodeUnitEnum(
            fields.representation,
            CB_REPRESENTATIONS,
            'imported lock representation',
        ),
    };
};

export const parseCbPage = (
    value: xdr.ScVal | undefined,
): TimelockPage<CbPosition, CbPageEnvelope> => {
    const fields = decodeStruct(
        value,
        ['envelope', 'exhausted', 'items', 'next_cursor'] as const,
        'imported lock page',
    );
    const envelope = decodeStruct(
        fields.envelope,
        ['active_cb_count', 'cb_index_revision'] as const,
        'imported lock page envelope',
    );

    return {
        items: decodeVec(fields.items, 'imported lock page items').map(parseCbPosition),
        nextCursor: decodeU32(fields.next_cursor, 'imported lock page cursor'),
        exhausted: decodeBool(fields.exhausted, 'imported lock page exhausted'),
        envelope: {
            cbIndexRevision: decodeU64(envelope.cb_index_revision, 'imported lock revision'),
            activeCbCount: decodeU32(envelope.active_cb_count, 'imported lock count'),
        },
    };
};

// create_lock returns the new position id (u32).
export const parseCreatedPositionId = (value: unknown): number =>
    xdr.ScVal.is(value) ? decodeU32(value, 'created position id') : fail('missing result');

// withdraw returns the withdrawn amount (u64 stroops).
export const parseWithdrawnAmount = (value: unknown): bigint =>
    xdr.ScVal.is(value) ? decodeU64(value, 'withdrawn amount') : fail('missing result');

interface PagingSpec<Item, Envelope> {
    fetchPage: (cursor: number, expected: Envelope | null) => Promise<TimelockPage<Item, Envelope>>;
    count: (envelope: Envelope) => number;
    revision: (envelope: Envelope) => bigint;
    itemKey: (item: Item) => string | number;
    isStaleError: (error: unknown) => boolean;
}

const readPagesOnce = async <Item, Envelope>({
    fetchPage,
    count,
    revision,
    itemKey,
}: PagingSpec<Item, Envelope>): Promise<Item[]> => {
    const items: Item[] = [];
    const keys = new Set<string | number>();
    let pinned: Envelope | null = null;
    let cursor = 0;

    for (;;) {
        const page = await fetchPage(cursor, pinned);
        const envelope = page.envelope;

        if (
            pinned &&
            (revision(pinned) !== revision(envelope) || count(pinned) !== count(envelope))
        ) {
            throw new TimelockPagingError('the envelope changed between pages');
        }
        pinned = envelope;

        if (page.items.length > TIMELOCK_PAGE_LIMIT) {
            throw new TimelockPagingError('a page is larger than the requested limit');
        }
        if (page.nextCursor !== cursor + page.items.length) {
            throw new TimelockPagingError('the cursor does not match the returned items');
        }
        if (page.nextCursor > count(envelope)) {
            throw new TimelockPagingError('the cursor is past the active count');
        }
        if (page.exhausted !== (page.nextCursor === count(envelope))) {
            throw new TimelockPagingError('the exhausted flag does not match the cursor');
        }
        if (!page.exhausted && !page.items.length) {
            throw new TimelockPagingError('a page made no progress');
        }

        page.items.forEach(item => {
            const key = itemKey(item);

            if (keys.has(key)) {
                throw new TimelockPagingError(`duplicate item ${key}`);
            }
            keys.add(key);
            items.push(item);
        });

        if (page.exhausted) {
            break;
        }
        cursor = page.nextCursor;
    }

    if (items.length !== count(pinned)) {
        throw new TimelockPagingError('the item count does not match the active count');
    }
    return items;
};

// Reads every page of an owner's active index: the first request carries no envelope, every
// later one carries the envelope of the first page, and the pages must add up to exactly the
// active count with unique items. A stale envelope restarts the read from the first page.
export const readAllTimelockPages = async <Item, Envelope>(
    spec: PagingSpec<Item, Envelope>,
): Promise<Item[]> => {
    for (let attempt = 1; ; attempt += 1) {
        try {
            return await readPagesOnce(spec);
        } catch (error) {
            if (attempt >= MAX_PAGING_ATTEMPTS || !spec.isStaleError(error)) {
                throw error;
            }
        }
    }
};

const errorIncludes = (error: unknown, code: string): boolean =>
    (error instanceof Error ? error.message : String(error)).includes(code);

const positionIdToScVal = (positionId: number): xdr.ScVal => {
    if (!Number.isInteger(positionId) || positionId < 0 || positionId > U32_MAX) {
        throw new Error(`Invalid position id: ${positionId}`);
    }
    return xdr.ScVal.scvU32(positionId);
};

const directEnvelopeToScVal = (envelope: DirectPageEnvelope | null): xdr.ScVal =>
    envelope
        ? xdr.ScVal.scvMap([
              new xdr.ScMapEntry({
                  key: xdr.ScVal.scvSymbol('active_direct_count'),
                  val: xdr.ScVal.scvU32(envelope.activeDirectCount),
              }),
              new xdr.ScMapEntry({
                  key: xdr.ScVal.scvSymbol('direct_index_revision'),
                  val: u64ToScVal(envelope.directIndexRevision),
              }),
          ])
        : xdr.ScVal.scvVoid();

const cbEnvelopeToScVal = (envelope: CbPageEnvelope | null): xdr.ScVal =>
    envelope
        ? xdr.ScVal.scvMap([
              new xdr.ScMapEntry({
                  key: xdr.ScVal.scvSymbol('active_cb_count'),
                  val: xdr.ScVal.scvU32(envelope.activeCbCount),
              }),
              new xdr.ScMapEntry({
                  key: xdr.ScVal.scvSymbol('cb_index_revision'),
                  val: u64ToScVal(envelope.cbIndexRevision),
              }),
          ])
        : xdr.ScVal.scvVoid();

export default class TimelockContract {
    private readonly connection: Connection;

    readonly transactions = new LockerTransactions();

    constructor(connection: Connection) {
        this.connection = connection;
    }

    private get contractId(): string {
        return CONTRACTS[getEnv()].tokenTimelock;
    }

    private get batchContractId(): string {
        return CONTRACTS[getEnv()].batch;
    }

    // expectedErrors: simulation errors the caller handles itself, kept out of Sentry.
    private async read(
        method: TIMELOCK_CONTRACT_METHOD,
        args: xdr.ScVal[],
        expectedErrors: string[] = [],
    ) {
        const tx = await this.connection.buildSmartContractTxForSimulate(
            ACCOUNT_FOR_SIMULATE,
            this.contractId,
            method,
            ...args,
        );
        const response: rpc.Api.SimulateTransactionResponse = await this.connection.simulateTx(tx, {
            expectedErrors,
        });

        if (rpc.Api.isSimulationError(response)) {
            throw new Error(response.error);
        }
        if (!response.result?.retval) {
            throw new TimelockDecodeError(`${method} returned no result`);
        }
        return response.result.retval;
    }

    // Page reads: a stale envelope is retried by readAllTimelockPages, so it is not reported.
    getPositionsPage(owner: string, cursor: number, expected: DirectPageEnvelope | null) {
        return this.read(
            TIMELOCK_CONTRACT_METHOD.GET_POSITIONS_PAGE,
            [
                publicKeyToScVal(owner),
                xdr.ScVal.scvU32(cursor),
                xdr.ScVal.scvU32(TIMELOCK_PAGE_LIMIT),
                directEnvelopeToScVal(expected),
            ],
            [STALE_DIRECT_CURSOR_ERROR],
        ).then(parseDirectPage);
    }

    getCbPage(owner: string, cursor: number, expected: CbPageEnvelope | null) {
        return this.read(
            TIMELOCK_CONTRACT_METHOD.GET_CB_PAGE,
            [
                publicKeyToScVal(owner),
                xdr.ScVal.scvU32(cursor),
                xdr.ScVal.scvU32(TIMELOCK_PAGE_LIMIT),
                cbEnvelopeToScVal(expected),
            ],
            [STALE_CB_CURSOR_ERROR],
        ).then(parseCbPage);
    }

    // All active direct positions of the owner (expired but not yet withdrawn included).
    getAllDirectPositions(owner: string): Promise<DirectPosition[]> {
        return readAllTimelockPages<DirectPosition, DirectPageEnvelope>({
            fetchPage: (cursor, expected) => this.getPositionsPage(owner, cursor, expected),
            count: envelope => envelope.activeDirectCount,
            revision: envelope => envelope.directIndexRevision,
            itemKey: position => position.positionId,
            isStaleError: error => errorIncludes(error, STALE_DIRECT_CURSOR_ERROR),
        });
    }

    // All active imported Classic locks of the owner.
    getAllCbPositions(owner: string): Promise<CbPosition[]> {
        return readAllTimelockPages<CbPosition, CbPageEnvelope>({
            fetchPage: (cursor, expected) => this.getCbPage(owner, cursor, expected),
            count: envelope => envelope.activeCbCount,
            revision: envelope => envelope.cbIndexRevision,
            itemKey: position => position.cbId,
            isStaleError: error => errorIncludes(error, STALE_CB_CURSOR_ERROR),
        });
    }

    async getDirectPosition(owner: string, positionId: number): Promise<DirectPositionStatus> {
        const retval = await this.read(TIMELOCK_CONTRACT_METHOD.GET_DIRECT_POSITION, [
            publicKeyToScVal(owner),
            positionIdToScVal(positionId),
        ]);
        return parseDirectPositionStatus(retval);
    }

    // Status of several positions in one batched simulation, in the order of positionIds.
    async getDirectPositions(
        owner: string,
        positionIds: number[],
    ): Promise<DirectPositionStatus[]> {
        const calls = positionIds.map(positionId =>
            buildBatchCall(this.contractId, TIMELOCK_CONTRACT_METHOD.GET_DIRECT_POSITION, [
                publicKeyToScVal(owner),
                positionIdToScVal(positionId),
            ]),
        );
        const results = await simulateBatch(
            this.connection,
            this.batchContractId,
            ACCOUNT_FOR_SIMULATE,
            calls,
            { maxCallsPerBatch: 10 },
        );

        if (results.length !== positionIds.length) {
            fail('the batched status read returned a different number of results');
        }
        return results.map(parseDirectPositionStatus);
    }

    // A created lock counts as confirmed only when create_lock returned a position id and that
    // position reads back as Active with the requested amount and unlock time.
    async confirmCreatedLock(
        owner: string,
        result: unknown,
        amountStroops: bigint,
        unlockSec: number,
    ): Promise<boolean> {
        try {
            const status = await this.getDirectPosition(owner, parseCreatedPositionId(result));

            return (
                status.status === 'Active' &&
                status.position.amount === amountStroops &&
                status.position.kind === 'Unlocking' &&
                status.position.end === unlockSec
            );
        } catch {
            return false;
        }
    }

    // A single withdrawal counts as confirmed only when withdraw returned an amount and the
    // position reads back as Closed with that amount.
    async confirmWithdrawal(owner: string, positionId: number, result: unknown): Promise<boolean> {
        try {
            const amount = parseWithdrawnAmount(result);
            const status = await this.getDirectPosition(owner, positionId);

            return status.status === 'Closed' && status.position.amount === amount;
        } catch {
            return false;
        }
    }

    // A batched withdrawal counts as confirmed only when every position reads back as Closed.
    async confirmWithdrawn(owner: string, positionIds: number[]): Promise<boolean> {
        try {
            const statuses = await this.getDirectPositions(owner, positionIds);

            return statuses.every(
                (status, index) =>
                    status.status === 'Closed' && status.position.positionId === positionIds[index],
            );
        } catch {
            return false;
        }
    }

    // Aggregate current ICE power of the owner (u64 stroops).
    balance(owner: string): Promise<bigint> {
        return this.read(TIMELOCK_CONTRACT_METHOD.BALANCE, [publicKeyToScVal(owner)]).then(retval =>
            decodeU64(retval, 'balance'),
        );
    }

    private createLockArgs(owner: string, amountStroops: bigint, unlockSec: number): xdr.ScVal[] {
        return [publicKeyToScVal(owner), u64ToScVal(amountStroops), u64ToScVal(unlockSec)];
    }

    private createLockRequest(owner: string, amountStroops: bigint, unlockSec: number) {
        return {
            source: owner,
            owner,
            contract: this.contractId,
            calls: [
                {
                    method: TIMELOCK_CONTRACT_METHOD.CREATE_LOCK,
                    args: this.createLockArgs(owner, amountStroops, unlockSec).map(value =>
                        value.toXdr('base64'),
                    ),
                },
            ],
        };
    }

    preflightCreateLock(owner: string, amountStroops: bigint, unlockSec: number) {
        return this.transactions.preflight(this.createLockRequest(owner, amountStroops, unlockSec));
    }

    async getCreateLockTx(
        owner: string,
        amountStroops: bigint,
        unlockSec: number,
        signer?: LockerTransactionSigner,
    ) {
        return await this.transactions.prepare(
            this.createLockRequest(owner, amountStroops, unlockSec),
            signer,
        );
    }

    // Simulates create_lock for the owner with the same arguments, without building a
    // transaction to sign, so a lock the Locker would reject is stopped before anything is
    // signed; rejects with the user-facing message of the simulation error.
    async simulateCreateLock(
        owner: string,
        amountStroops: bigint,
        unlockSec: number,
    ): Promise<void> {
        this.transactions.assertConfigured();
        const tx = await this.connection.buildSmartContractTxForSimulate(
            owner,
            this.contractId,
            TIMELOCK_CONTRACT_METHOD.CREATE_LOCK,
            ...this.createLockArgs(owner, amountStroops, unlockSec),
        );
        const response: rpc.Api.SimulateTransactionResponse = await this.connection.simulateTx(tx);

        if (rpc.Api.isSimulationError(response)) {
            throw SorobanPrepareTxErrorHandler(response.error);
        }
    }

    preflightWithdraw(owner: string, positionIds: number[]) {
        return this.transactions.preflight(this.withdrawRequest(owner, positionIds));
    }

    async getWithdrawTx(owner: string, positionId: number, signer?: LockerTransactionSigner) {
        return await this.transactions.prepare(this.withdrawRequest(owner, [positionId]), signer);
    }

    private withdrawRequest(owner: string, positionIds: number[]) {
        return {
            source: owner,
            owner,
            contract: this.contractId,
            batcher: this.batchContractId,
            calls: positionIds.map(positionId => ({
                method: TIMELOCK_CONTRACT_METHOD.WITHDRAW,
                args: [publicKeyToScVal(owner), positionIdToScVal(positionId)].map(value =>
                    value.toXdr('base64'),
                ),
            })),
        };
    }

    async getWithdrawBatchTx(
        owner: string,
        positionIds: number[],
        signer?: LockerTransactionSigner,
    ) {
        return await this.transactions.prepare(this.withdrawRequest(owner, positionIds), signer);
    }

    simulateWithdrawBatch(owner: string, positionIds: number[]): Promise<void> {
        return this.transactions.preview(this.withdrawRequest(owner, positionIds));
    }
}
